import React from 'react';
import useAutoFocusFirstField from 'hooks/useAutoFocusFirstField';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, TextField } from '@material-ui/core';

import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';
import SaveError from 'components/common/SaveError';
import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import { formatMoney } from 'utils/formatMoney';
import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getVenueProductionCenters } from 'services/stockProductionCenterServices';
import { getRecipesSimple } from 'services/stockRecipeServices';
import { storeProduction } from 'services/stockProductionServices';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';
import Venue from 'types/models/Venue';
import { StockProductionCenter, StockProductType } from 'types/models/StockReceipt';
import { StockMovement, StockMovementOperation, StockRecipe } from 'types/models/StockMovement';
import { StockProductionFormSchema } from './schemas/StockModuleForm';

interface Props {
  movement?: StockMovement | null;
  isReadOnly?: boolean;
}

export default function StockProductionForm({ movement, isReadOnly }: Props): JSX.Element {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [openDialog, setOpenDialog] = React.useState(false);
  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [recipes, setRecipes] = React.useState<StockRecipe[]>([]);
  const [productionCenters, setProductionCenters] = React.useState<StockProductionCenter[]>([]);

  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const formRef = useAutoFocusFirstField(!isLoading && !loadError);

  const finishedItem = movement?.items?.find((item) => item.operation === StockMovementOperation.INCREASE);
  const ingredientItems = movement?.items?.filter((item) => item.operation === StockMovementOperation.DECREASE) ?? [];

  React.useEffect(() => {
    let mounted = true;

    async function loadInitialData() {
      if (isVenueEditor) {
        if (mounted) setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const venuesResponse = await getVenuesWithStockModuleActive();

        if (!mounted) return;

        setVenues(venuesResponse?.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      venueId: movement?.venueId ?? entity?.id ?? '',
      recipeId: movement?.recipeId ?? '',
      productionCenterId: movement?.sourceId ?? '',
      quantity: finishedItem?.quantity ?? '',
    },
    validationSchema: () => StockProductionFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setRecipes([]);
      setProductionCenters([]);
      return;
    }

    async function loadVenueData() {
      try {
        const [recipesData, centersData] = await Promise.all([
          getRecipesSimple(formik.values.venueId),
          getVenueProductionCenters(formik.values.venueId),
        ]);

        setRecipes(recipesData);
        setProductionCenters(centersData);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenueData();
  }, [formik.values.venueId]);

  const selectedRecipe = recipes.find((recipe) => recipe.id === formik.values.recipeId);

  const estimatedUnitCost = React.useMemo(() => {
    if (!selectedRecipe?.items) return 0;

    return selectedRecipe.items.reduce(
      (sum, item) => sum + (Number(item.ingredient?.averageCost) || 0) * (Number(item.quantity) || 0),
      0,
    );
  }, [selectedRecipe]);

  const productionQuantity = Number(formik.values.quantity) || 0;
  const estimatedTotalCost = estimatedUnitCost * productionQuantity;

  async function onConfirm() {
    setSaveError(null);

    if (!selectedRecipe) return;

    try {
      await storeProduction({
        venueId: formik.values.venueId,
        globalProductId: Number(selectedRecipe.globalProductId),
        productionCenterId: formik.values.productionCenterId,
        quantity: Number(formik.values.quantity),
        createdByUserId: authData?.user.id,
      });
      history.go(-1);
    } catch (error) {
      console.error(error);

      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));

      formik.setSubmitting(false);
    }
  }

  if (isLoading) {
    return <div>{t('loading')}</div>;
  }

  if (loadError) {
    return <div>{loadError}</div>;
  }

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <h1>{isReadOnly ? t('stock.productions.detail.title') : t('stock.productions.form.title')}</h1>

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.productions.form.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, {
              label: 'name',
            })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting || !!movement}
            isSearchable
          />
        )}

        <SelectField
          name="recipeId"
          label={t('stock.productions.form.recipe')}
          placeholder={t('stock.productions.form.recipe-placeholder')}
          value={formik.values.recipeId}
          options={
            movement?.recipe?.productName
              ? [
                  ...recipes.map((recipe) => ({
                    value: recipe.id,
                    label: recipe.globalProduct?.name ?? String(recipe.globalProductId),
                  })),
                  ...(formik.values.recipeId && !recipes.some((r) => r.id === formik.values.recipeId)
                    ? [{ value: formik.values.recipeId, label: movement.recipe.productName }]
                    : []),
                ]
              : recipes.map((recipe) => ({
                  value: recipe.id,
                  label: recipe.globalProduct?.name ?? String(recipe.globalProductId),
                }))
          }
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.recipeId}
          error={formik.errors.recipeId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting || !!movement}
          isSearchable
        />

        <SelectField
          name="productionCenterId"
          label={t('stock.productions.form.production-center')}
          placeholder={t('stock.productions.form.production-center-placeholder')}
          value={formik.values.productionCenterId}
          options={
            movement?.source?.name
              ? [
                  ...mapToSelectOptions(productionCenters, {
                    label: 'name',
                  }),
                  ...(formik.values.productionCenterId
                    ? [{ value: formik.values.productionCenterId, label: movement.source.name }]
                    : []),
                ]
              : mapToSelectOptions(productionCenters, {
                  label: 'name',
                })
          }
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.productionCenterId}
          error={formik.errors.productionCenterId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting || !!movement}
          isSearchable
        />

        <TextField
          margin="dense"
          fullWidth
          type="number"
          name="quantity"
          label={t('stock.productions.form.quantity')}
          placeholder={t('stock.productions.form.quantity-placeholder')}
          value={formik.values.quantity}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik.touched.quantity && formik.errors.quantity)}
          helperText={formik.touched.quantity && formik.errors.quantity}
          inputProps={{
            min: 1,
            max: 99999,
            step: 1,
            readOnly: !!movement,
          }}
        />

        {!isReadOnly && (
          <p
            style={{
              fontSize: 13,
              color: '#757575',
              marginTop: 10,
            }}
          >
            {t('stock.productions.form.warehouse-info')}
          </p>
        )}

        <hr />

        <h3>{t('stock.productions.form.ingredients-title')}</h3>

        {/* aca */}
        {movement ? (
          <div>
            <div
              style={{
                padding: '8px 12px',
                marginBottom: 8,
                border: '1px solid #E0E0E0',
                borderRadius: 4,
                backgroundColor: '#E3F2FD',
              }}
            >
              <div style={{ fontWeight: 600 }}>{t('stock.productions.detail.finished-product')}</div>
              <div style={{ fontSize: 13, color: '#424242', marginTop: 2 }}>
                {finishedItem?.globalProduct?.name ?? '—'} · {finishedItem?.quantity ?? '—'}{' '}
                {finishedItem?.globalProduct?.unitMeasure
                  ? t(`stock.units.${finishedItem.globalProduct.unitMeasure}`)
                  : ''}
              </div>
            </div>

            {ingredientItems.map((item) => {
              const gp = item.globalProduct;
              const receptionUnit = gp?.receptionUnitMeasure ? t(`stock.units.${gp.receptionUnitMeasure}`) : '';
              const consumptionUnit = gp?.unitMeasure ? t(`stock.units.${gp.unitMeasure}`) : '';
              // En el detalle, item.quantity ya está en unidades de recepción (lo que se descontó del depósito).
              const withdrawnInConsumption =
                receptionUnit && gp?.unitsPerReceptionUnit
                  ? Number(item.quantity) * Number(gp.unitsPerReceptionUnit)
                  : null;

              return (
                <div
                  key={item.id}
                  style={{
                    padding: '8px 12px',
                    marginBottom: 8,
                    border: '1px solid #E0E0E0',
                    borderRadius: 4,
                    backgroundColor: '#FAFAFA',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{gp?.name ?? '—'}</div>
                  <div style={{ fontSize: 13, color: '#757575', marginTop: 2 }}>
                    {t('stock.productions.form.ingredient-withdrawn', {
                      quantity: Number(item.quantity),
                      unit: receptionUnit || '',
                    })}
                    {withdrawnInConsumption != null && (
                      <>
                        {' · '}
                        {t('stock.productions.form.ingredient-consumption-equivalent', {
                          total: withdrawnInConsumption,
                          unit: consumptionUnit || '',
                        })}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : selectedRecipe?.items && selectedRecipe.items.length > 0 ? (
          <div>
            {selectedRecipe.items.map((item) => {
              const ingredient = item.ingredient;
              const consumptionUnit = ingredient?.unitMeasure ? t(`stock.units.${ingredient.unitMeasure}`) : '';
              const outputUnit = selectedRecipe?.globalProduct?.unitMeasure
                ? t(`stock.units.${selectedRecipe.globalProduct.unitMeasure}`)
                : 'unidad';
              const receptionUnit = ingredient?.receptionUnitMeasure
                ? t(`stock.units.${ingredient.receptionUnitMeasure}`)
                : '';
              const isRaw = ingredient?.type === StockProductType.RAW_MATERIAL;
              const showReception =
                isRaw && ingredient?.receptionUnitMeasure && ingredient?.unitsPerReceptionUnit != null;

              const totalConsumption = Number(item.quantity) * productionQuantity;
              // Cuántas unidades de recepción (botellas/paquetes reales) se descuentan del depósito.
              const totalReceptionUnits =
                showReception && ingredient?.unitsPerReceptionUnit
                  ? totalConsumption / Number(ingredient.unitsPerReceptionUnit)
                  : null;

              return (
                <div
                  key={item.id}
                  style={{
                    padding: '8px 12px',
                    marginBottom: 8,
                    border: '1px solid #E0E0E0',
                    borderRadius: 4,
                    backgroundColor: '#FAFAFA',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{ingredient?.name ?? '—'}</div>
                  <div style={{ fontSize: 13, color: '#757575', marginTop: 2 }}>
                    {t('stock.productions.form.ingredient-consumes', {
                      quantity: Number(item.quantity),
                      unit: consumptionUnit || '',
                      outputUnit,
                    })}
                    {showReception && (
                      <>
                        <br />
                        <span>
                          ·{' '}
                          {t('stock.productions.form.ingredient-received-as', {
                            receptionUnit,
                            perUnit: ingredient?.unitsPerReceptionUnit,
                            consumptionUnit: consumptionUnit || t('stock.units.UNIT'),
                          })}
                        </span>
                      </>
                    )}
                    {productionQuantity > 0 && (
                      <>
                        <br />
                        <strong>
                          {t('stock.productions.form.ingredient-total', {
                            total: totalConsumption,
                            unit: consumptionUnit || '',
                          })}
                        </strong>
                        {totalReceptionUnits != null && (
                          <>
                            {' · '}
                            <strong>
                              {t('stock.productions.form.ingredient-reception-equivalent', {
                                total: totalReceptionUnits,
                                receptionUnit,
                              })}
                            </strong>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            <div
              style={{
                padding: 12,
                marginTop: 12,
                background: '#F5F5F5',
                borderRadius: 4,
              }}
            >
              <div style={{ fontSize: 13, color: '#424242' }}>
                <strong>{t('stock.productions.form.estimated-unit-cost')}: </strong>
                {formatMoney(estimatedUnitCost, i18n.language as any)}
              </div>
              <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
                {t('stock.productions.form.estimated-unit-cost-note')}
              </div>
              <div style={{ fontSize: 13, color: '#424242', marginTop: 8 }}>
                <strong>{t('stock.productions.form.manufacturing-cost', { quantity: productionQuantity })}: </strong>
                {formatMoney(estimatedTotalCost, i18n.language as any)}
              </div>
            </div>
          </div>
        ) : (
          <p>{t('stock.recipes.table.no-results')}</p>
        )}

        <SaveError error={saveError} />
        {!isReadOnly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 25,
            }}
          >
            <Button variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
              {t('stock.productions.form.submit')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
